import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from './useHandleHttpRequestError';

const useGetExistingSiteData = () => {
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [existingSites, setExistingSites] = useState<
    Array<{ siteId: string; siteName: string }>
  >([]);

  const { t } = useTranslation(I18NextNS.COMMON);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const { currentUser } = useRemoteBoundStore();

  const getExistingSiteData = useCallback(async () => {
    try {
      setIsLoadingSites(true);
      const practiceId = currentUser?.currentRole?.practice?.practiceId;
      if (!practiceId) {
        throw new Error(t('message.error.practice-id-is-required'));
      }

      const response = await Api.remote.get_site_data();

      if (response.data.content && response.data.content.length > 0) {
        const allSites = response.data.content.map((item) => ({
          siteId: item.data.SiteId.value,
          siteName:
            item.data.SiteName.value.charAt(0).toUpperCase() +
            item.data.SiteName.value.slice(1),
        }));

        setExistingSites(
          allSites.sort((a, b) => a.siteName.localeCompare(b.siteName)),
        );
      } else {
        setExistingSites([]);
      }
    } catch (error) {
      showHttpRequestError(error);
    } finally {
      setIsLoadingSites(false);
    }
  }, [currentUser?.currentRole?.practice?.practiceId, showHttpRequestError, t]);

  return {
    isLoadingSites,
    existingSites,
    getExistingSiteData,
  };
};

export default useGetExistingSiteData;
